import React from "react";
import {Routes, Route, Navigate, useLocation} from "react-router-dom";
import {Layout, Skeleton} from "antd";

import AuthenticatedRoute from "../components/AuthenticatedRoute";
import EngageSidebar from "./EngageSidebar";
import EngageHeader from "./EngageHeader";
import EngageFooter from "./EngageFooter";
import Login from "../containers/Login";
import Logout from "../containers/Logout";
import {buildRoutes} from "../routes/buildRoutes";
import {setDayJSTimeZone} from "../lib/time";

import {useAppSelector} from "../hooks/useStore";
import {useIsAuthenticated} from "../hooks/useIsAuthenticated";
import {
    useGetBookingConfigQuery,
    useGetCompanyInfoQuery,
    useGetModuleConfigQuery,
    useGetServerTimeZoneQuery
} from "../store/api/settingsApi";
import {selectCurrentProfile, selectIsStaff} from "../store/slices/userSlice";
import {OverlayLoadingIndicator, setOverlayLoading} from "../components/OverlayLoadingIndicator";

import "./EngageLayout.less";

const {Content} = Layout;
const mobileBreakpoint = 768;

const EngageLayout = () => {
    const location = useLocation();
    const {isAuthenticated, isLoading} = useIsAuthenticated(true);
    const isStaff = useAppSelector(selectIsStaff);
    const profile = useAppSelector(selectCurrentProfile);
    const isBusiness = profile?.account?.is_business ?? false;
    const hasMembership = profile?.membership !== null;

    // Set config settings.
    useGetBookingConfigQuery();
    useGetCompanyInfoQuery();
    useGetModuleConfigQuery();
    const {data: serverTimeZone} = useGetServerTimeZoneQuery();

    //region Window Width
    const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
    const updateWindowDimensions = () => {
        setWindowWidth(window.innerWidth);
    };

    React.useEffect(() => {
        // Necessary for when a user manually refreshes a page (and thus reinitialize DayJS/Moment)
        setDayJSTimeZone(serverTimeZone);

        window.addEventListener('resize', updateWindowDimensions);

        return function cleanup() {
            window.removeEventListener('resize', updateWindowDimensions);
        }
    }, [serverTimeZone]);
    //endregion

    //region Mobile Mode
    const [mobileMode, setMobileMode] = React.useState(false);
    React.useEffect(() => {
        if (windowWidth < mobileBreakpoint) {
            setMobileMode(true)
        } else {
            setMobileMode(false);
        }
    }, [windowWidth]);
    //endregion

    //region Side Menu Expand/Collapse state
    const [sideMenuCollapsed, setSideMenuCollapsed] = React.useState(false);
    const [sideMenuMounted, setSideMenuMounted] = React.useState(false);

    const toggleSideMenu = React.useCallback(() => {
        setSideMenuCollapsed(!sideMenuCollapsed);
    }, [sideMenuCollapsed])

    React.useEffect(() => {
        if (!sideMenuMounted && mobileMode){
            toggleSideMenu();
            setSideMenuMounted(true);
        }
    }, [toggleSideMenu, sideMenuMounted, mobileMode]);

    // When a user changes the url on mobile we want to collapse the side menu.
    React.useEffect(() => {
        if (window.innerWidth <= mobileBreakpoint) {
            setSideMenuCollapsed(true);
        }
    }, [location]);
    //endregion

    //region Footer state
    // We want to hide the footer on the mobile CarMap page as the real estate is very limited and valuable.
    // To do that we check the url path name and the width of the page.
    const hideFooter = location.pathname === '/cars/map' ? window.innerWidth <= mobileBreakpoint : false;
    //endregion

    // We want to memoize all routes to restrict a rerender of the entire component tree to only when user state is updated.
    // But memoizing here happens on the login screen even before the user logs in. This is a problem as both the Invoice
    //  and Billing components/sidebarLinks have a module property that rely on the getModuleConfig() promise as part of the
    //  login process, so these components are memoized as null causing them to appear as 404 pages when the user navigates to
    //  them for the first time. This issue doesn't appear to have a straight forward solution, so will be pushed to a
    //  separate story that may involve refactoring buildRoutes flow slightly to allow memoization of the routes.
    // const routes = React.useMemo(() => {
    //     buildRoutes({isStaff, hasMembership, isBusiness});
    // }, [isStaff, hasMembership, isBusiness]);
    const routes = buildRoutes({isStaff, hasMembership, isBusiness});

    // If the isAuthenticated is still loading then return a skeleton loading img, otherwise login doesn't work.
    if (isLoading) {
        return <Skeleton />;
    }

    // Only display sidebar, header and footer when user is authenticated, otherwise the login menu would show these.
    return (
        <>
            <Layout className="layout-site">
                {
                    isAuthenticated &&
                    <EngageSidebar
                        mobileMode={mobileMode}
                        sideMenuCollapsed={sideMenuCollapsed}
                        toggleSideMenu={toggleSideMenu}
                        isStaff={isStaff}
                        isBusiness={isBusiness}
                        hasMembership={hasMembership}
                    />
                }
                <Layout className="layout-content">
                    {
                        isAuthenticated &&
                        <EngageHeader
                            mobileMode={mobileMode}
                            toggleSideMenu={toggleSideMenu}
                            isStaff={isStaff}
                            profile={profile}
                        />
                    }
                    <Content className="main-content">
                        <div className="main-content-div">
                            <Routes>
                                <Route path="/login" element={isAuthenticated ? <Navigate to="/booking/list" replace /> : <Login />} />
                                <Route element={<AuthenticatedRoute isAuthenticated={isAuthenticated} />}>
                                    {routes}
                                    <Route path="/logout" element={<Logout/>} />
                                </Route>
                            </Routes>
                        </div>
                        {
                            isAuthenticated && !hideFooter && <EngageFooter/>
                        }
                    </Content>
                </Layout>
            </Layout>
            <OverlayLoadingIndicator ref={setOverlayLoading} initialVisibility={true} />
        </>
    );
};

export default EngageLayout;
